import { motion } from "framer-motion";
import { Mic, Music, Moon } from "lucide-react";
import ModePreviewButton from "./ModePreviewButton";

/**
 * Reclaim Mode Selector — 3 breathing orbs
 *
 * CORE PRINCIPLE: "Users do not build sessions. Users enter a state."
 *
 * Three modes:
 *   • Clear  — voice-led
 *   • Soft   — recommended (immersive)
 *   • Deep   — subconscious / sleep
 */

const MODES = [
  {
    id: "clear",
    label: "Clear",
    tagline: "Voice-led",
    description: "I hear the voice",
    icon: Mic,
    color: "#D6D3CC",       // soft silver
    glow: "rgba(214,211,204,0.35)",
    recommended: false,
  },
  {
    id: "soft",
    label: "Soft",
    tagline: "Best for daily use",
    description: "Immersive experience",
    icon: Music,
    color: "#CBA153",       // gold
    glow: "rgba(203,161,83,0.45)",
    recommended: true,
  },
  {
    id: "deep",
    label: "Deep",
    tagline: "Best for sleep",
    description: "Subconscious / sleep",
    icon: Moon,
    color: "#8B92F2",       // soft indigo
    glow: "rgba(139,146,242,0.4)",
    recommended: false,
  },
];

export default function ModeSelector({ selected = "soft", onChange, compact = false }) {
  return (
    <div
      className={`flex items-stretch justify-center gap-4 ${compact ? "" : "py-8"}`}
      data-testid="mode-selector"
    >
      {MODES.map((m, idx) => {
        const Icon = m.icon;
        const active = selected === m.id;
        const orbSize = compact ? 60 : 92;
        return (
          <motion.button
            key={m.id}
            onClick={() => onChange(m.id)}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.12, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            whileTap={{ scale: 0.96 }}
            className="flex flex-col items-center text-center px-2 transition-all"
            style={{ flex: compact ? "0 0 auto" : "1 1 0" }}
            data-testid={`mode-${m.id}`}
          >
            {/* Breathing orb */}
            <motion.div
              animate={
                active
                  ? { scale: [1, 1.08, 1], opacity: [0.92, 1, 0.92] }
                  : { scale: 1, opacity: 0.7 }
              }
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              className="relative rounded-full flex items-center justify-center mb-3"
              style={{
                width: orbSize,
                height: orbSize,
                background: active
                  ? `radial-gradient(circle, ${m.color}38 0%, transparent 65%)`
                  : "rgba(245,240,230,0.03)",
                border: `1px solid ${active ? m.color + "66" : "rgba(245,240,230,0.08)"}`,
              }}
            >
              <Icon
                size={compact ? 18 : 26}
                strokeWidth={1.3}
                style={{ color: active ? m.color : "rgba(245,240,230,0.55)" }}
              />
              {active && (
                <div
                  className="absolute inset-0 rounded-full pointer-events-none -z-10 blur-xl"
                  style={{ background: m.glow }}
                />
              )}
              {m.recommended && (
                <span
                  className="absolute -top-1 right-0 text-[8px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full font-light"
                  style={{
                    background: `${m.color}22`,
                    color: m.color,
                    border: `1px solid ${m.color}55`,
                  }}
                >
                  ✦
                </span>
              )}
            </motion.div>

            {/* Label */}
            <p
              className={compact ? "text-[11px]" : "text-sm"}
              style={{
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                color: active ? m.color : "rgba(245,240,230,0.7)",
                fontWeight: 300,
              }}
            >
              {m.label}
            </p>
            {!compact && (
              <p
                className="text-[10px] uppercase tracking-[0.2em] mt-1 font-light"
                style={{ color: active ? `${m.color}99` : "rgba(245,240,230,0.35)" }}
              >
                {m.tagline}
              </p>
            )}
            {!compact && (
              <div
                className="mt-2"
                onClick={(e) => e.stopPropagation()}
              >
                <ModePreviewButton mode={m.id} accent={m.color} size={24} />
              </div>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}

export { MODES };
